import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { AppLayout } from "@/components/app-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/components/language-provider";
import { apiRequest } from "@/lib/queryClient";
import { getApiUrl } from "@/lib/config";
import {
  History, FileText, User, Calendar, Search, RefreshCw, Filter,
  ChevronRight, Eye, Clock, ArrowLeft, ArrowRight, Download,
  FileSpreadsheet, Loader2, Pen, XCircle, CheckCheck, Send, Trash2, Upload, Bell, ArrowUpDown
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";

interface AuditLog {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorId: string | null;
  actorEmail: string | null;
  actorName: string | null;
  actorIp: string | null;
  actorUserAgent: string | null;
  companyId: string | null;
  description: string | null;
  documentName: string | null;
  beforeSnapshot: Record<string, any> | null;
  afterSnapshot: Record<string, any> | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const ACTION_ICONS: Record<string, typeof FileText> = {
  DocumentCreated: Upload,
  DocumentUploaded: Upload,
  DocumentDeleted: Trash2,
  DocumentViewed: Eye,
  DocumentCompleted: CheckCheck,
  SignatureRequestCreated: Send,
  SignatureRequestSent: Send,
  SignatureRequestCancelled: XCircle,
  SignatureRequestExpired: Clock,
  SignatureApplied: Pen,
  SignatureDeclined: XCircle,
  SigningPageOpened: Eye,
  SignerAdded: User,
  AuditPdfGenerated: FileText,
  SignatureExpired: Clock,
  CopySentToSigner: Send,
  SigningLinkResent: Send,
  DocumentSentToSigner: Send,
  ReminderSent: Bell,
  SigningOrderChanged: ArrowUpDown,
};

const ACTION_COLORS: Record<string, string> = {
  DocumentCreated: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  DocumentUploaded: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  DocumentDeleted: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  DocumentViewed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  DocumentCompleted: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  SignatureRequestCreated: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  SignatureRequestSent: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  SignatureRequestCancelled: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  SignatureRequestExpired: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  SignatureApplied: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  SignatureDeclined: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  SigningPageOpened: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  SignatureExpired: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  CopySentToSigner: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  SigningLinkResent: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  DocumentSentToSigner: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  ReminderSent: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  SigningOrderChanged: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

export default function AuditLogs() {
  const [, setLocation] = useLocation();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [entityType, setEntityType] = useState<string>("all");
  const [action, setAction] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [exporting, setExporting] = useState(false);

  const locale = language === "pt" ? ptBR : enUS;

  const tr = {
    title: language === "pt" ? "Registros de Auditoria" : "Audit Logs",
    subtitle: language === "pt" ? "Histórico completo de atividades da empresa" : "Complete activity history for your organization",
    filters: language === "pt" ? "Filtros" : "Filters",
    entityType: language === "pt" ? "Tipo" : "Type",
    actionLabel: language === "pt" ? "Ação" : "Action",
    from: language === "pt" ? "De" : "From",
    to: language === "pt" ? "Até" : "To",
    search: language === "pt" ? "Buscar por email ou nome..." : "Search by email or name...",
    clearFilters: language === "pt" ? "Limpar Filtros" : "Clear Filters",
    refresh: language === "pt" ? "Atualizar" : "Refresh",
    exportExcel: language === "pt" ? "Exportar Excel" : "Export Excel",
    exportPdf: language === "pt" ? "Exportar PDF" : "Export PDF",
    noLogs: language === "pt" ? "Nenhum registro encontrado" : "No audit logs found",
    noLogsDesc: language === "pt" ? "Ajuste os filtros ou volte mais tarde" : "Try adjusting your filters or check back later",
    details: language === "pt" ? "Detalhes do Registro" : "Audit Log Details",
    actor: language === "pt" ? "Ator" : "Actor",
    ipAddress: language === "pt" ? "Endereço IP" : "IP Address",
    document: language === "pt" ? "Documento" : "Document",
    timestamp: language === "pt" ? "Data/Hora" : "Timestamp",
    allTypes: language === "pt" ? "Todos os tipos" : "All types",
    allActions: language === "pt" ? "Todas as ações" : "All actions",
    documents: language === "pt" ? "Documentos" : "Documents",
    signatures: language === "pt" ? "Assinaturas" : "Signatures",
    users: language === "pt" ? "Usuários" : "Users",
    previous: language === "pt" ? "Anterior" : "Previous",
    next: language === "pt" ? "Próximo" : "Next",
    page: language === "pt" ? "Página" : "Page",
    of: language === "pt" ? "de" : "of",
    showing: language === "pt" ? "Exibindo" : "Showing",
    entries: language === "pt" ? "registros" : "entries",
    system: language === "pt" ? "Sistema" : "System",
    description: language === "pt" ? "Descrição" : "Description",
    metadata: language === "pt" ? "Metadados" : "Metadata",
  };

  const actionLabels: Record<string, string> = language === "pt" ? {
    DocumentCreated: "Documento criado",
    DocumentUploaded: "Documento enviado",
    DocumentDeleted: "Documento excluído",
    DocumentViewed: "Documento visualizado",
    DocumentCompleted: "Documento concluído",
    SignatureRequestCreated: "Solicitação de assinatura criada",
    SignatureRequestSent: "Solicitação de assinatura enviada",
    SignatureRequestCancelled: "Solicitação cancelada",
    SignatureRequestExpired: "Solicitação expirada",
    SignatureApplied: "Assinatura aplicada",
    SignatureDeclined: "Assinatura recusada",
    SigningPageOpened: "Página de assinatura aberta",
    SignerAdded: "Assinante adicionado",
    SignerUpdated: "Assinante atualizado",
    SignerRemoved: "Assinante removido",
    AuditPdfGenerated: "PDF de auditoria gerado",
    SignatureExpired: "Assinatura expirada",
    DocumentReplaced: "Documento substituído",
    CopySentToSigner: "Cópia assinada reenviada ao signatário",
    SigningLinkResent: "Link de assinatura reenviado",
    DocumentSentToSigner: "Documento enviado ao assinante",
    ReminderSent: "Lembrete enviado",
    SigningOrderChanged: "Ordem de assinatura alterada",
  } : {
    DocumentCreated: "Document created",
    DocumentUploaded: "Document uploaded",
    DocumentDeleted: "Document deleted",
    DocumentViewed: "Document viewed",
    DocumentCompleted: "Document completed",
    SignatureRequestCreated: "Signature request created",
    SignatureRequestSent: "Signature request sent",
    SignatureRequestCancelled: "Request cancelled",
    SignatureRequestExpired: "Request expired",
    SignatureApplied: "Signature applied",
    SignatureDeclined: "Signature declined",
    SigningPageOpened: "Signing page opened",
    SignerAdded: "Signer added",
    SignerUpdated: "Signer updated",
    SignerRemoved: "Signer removed",
    AuditPdfGenerated: "Audit PDF generated",
    SignatureExpired: "Signature expired",
    DocumentReplaced: "Document replaced",
    CopySentToSigner: "Signed copy resent to signer",
    SigningLinkResent: "Signing link resent",
    DocumentSentToSigner: "Document sent to signer",
    ReminderSent: "Reminder sent",
    SigningOrderChanged: "Signing order changed",
  };

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) { setLocation("/login"); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);

  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());
  queryParams.set("limit", "25");
  if (entityType && entityType !== "all") queryParams.set("entityType", entityType);
  if (action && action !== "all") queryParams.set("action", action);
  if (searchText) queryParams.set("actorId", searchText);
  if (dateFrom) queryParams.set("dateFrom", dateFrom);
  if (dateTo) queryParams.set("dateTo", dateTo);

  const { data, isLoading, refetch } = useQuery<AuditLogsResponse>({
    queryKey: [`/api/audit-logs?${queryParams.toString()}`],
    enabled: !!user,
  });

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    setLocation("/login");
  };

  const resetFilters = () => {
    setEntityType("all");
    setAction("all");
    setSearchText("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const exportToExcel = async () => {
    if (!data?.logs.length) return;
    setExporting(true);
    try {
      const headers = ["Date", "Action", "Actor", "Email", "Document", "IP Address", "Description"];
      const rows = data.logs.map(log => [
        format(new Date(log.createdAt), "yyyy-MM-dd HH:mm:ss"),
        actionLabels[log.action] || log.action,
        log.actorName || tr.system,
        log.actorEmail || "-",
        log.documentName || log.entityId?.slice(0, 8) || "-",
        log.actorIp || "-",
        log.description || "-",
      ]);
      const csvContent = [headers.join(","), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
      const bom = "\uFEFF";
      const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fillsign-audit-logs-${format(new Date(), "yyyy-MM-dd")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: language === "pt" ? "Exportado!" : "Exported!", description: language === "pt" ? "Arquivo CSV gerado" : "CSV file generated" });
    } catch { toast({ title: t("common.error"), description: t("ui.export_failed"), variant: "destructive" }); }
    finally { setExporting(false); }
  };

  if (!user) return null;

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/audit-logs">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <History className="h-6 w-6" /> {tr.title}
          </h1>
          <p className="text-muted-foreground">{tr.subtitle}</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">{tr.entityType}</Label>
                <Select value={entityType} onValueChange={(v) => { setEntityType(v); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{tr.allTypes}</SelectItem>
                    <SelectItem value="Document">{tr.documents}</SelectItem>
                    <SelectItem value="SignatureRequest">{tr.signatures}</SelectItem>
                    <SelectItem value="User">{tr.users}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{tr.actionLabel}</Label>
                <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{tr.allActions}</SelectItem>
                    {Object.entries(actionLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{tr.from}</Label>
                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{tr.to}</Label>
                <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{tr.search}</Label>
                <Input placeholder={tr.search} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={resetFilters}>
                  <Filter className="h-3 w-3 mr-1" /> {tr.clearFilters}
                </Button>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="h-3 w-3 mr-1" /> {tr.refresh}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={exportToExcel} disabled={!data?.logs.length || exporting}>
                  {exporting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3 w-3 mr-1" />}
                  {tr.exportExcel}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{tr.title}</CardTitle>
              {data?.pagination && (
                <span className="text-sm text-muted-foreground">
                  {tr.showing} {((data.pagination.page - 1) * data.pagination.limit) + 1}–{Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} {tr.of} {data.pagination.total} {tr.entries}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-3 border rounded-md">
                    <div className="h-10 w-10 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-1/3 bg-muted rounded" />
                      <div className="h-3 w-1/4 bg-muted rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !data?.logs?.length ? (
              <div className="text-center py-12">
                <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-1">{tr.noLogs}</h3>
                <p className="text-muted-foreground">{tr.noLogsDesc}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.logs.map((log) => {
                  const Icon = ACTION_ICONS[log.action] || FileText;
                  const colorClass = ACTION_COLORS[log.action] || "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400";

                  return (
                    <div
                      key={log.id}
                      className="flex items-center gap-4 p-3 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => { setSelectedLog(log); setShowDetailDialog(true); }}
                    >
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">
                            {log.actorName || log.actorEmail || tr.system}
                          </span>
                          <Badge variant="secondary" className={`text-xs ${colorClass}`}>
                            {actionLabels[log.action] || log.action}
                          </Badge>
                          {log.documentName && (
                            <span className="text-sm text-muted-foreground truncate max-w-48">
                              — {log.documentName}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <Clock className="h-3 w-3" />
                          <span>{formatDistanceToNow(new Date(log.createdAt), { addSuffix: true, locale })}</span>
                          <span className="text-muted-foreground/50">·</span>
                          <span>{format(new Date(log.createdAt), "dd/MM/yyyy HH:mm", { locale })}</span>
                          {log.actorIp && (
                            <>
                              <span className="text-muted-foreground/50">·</span>
                              <span>IP: {log.actorIp}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {data?.pagination && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ArrowLeft className="h-4 w-4 mr-1" /> {tr.previous}
                </Button>
                <span className="text-sm text-muted-foreground px-4">
                  {tr.page} {page} {tr.of} {data.pagination.totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={page === data.pagination.totalPages} onClick={() => setPage(p => p + 1)}>
                  {tr.next} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> {tr.details}
            </DialogTitle>
            {selectedLog && (
              <DialogDescription>
                {format(new Date(selectedLog.createdAt), "PPPp", { locale })}
              </DialogDescription>
            )}
          </DialogHeader>
          {selectedLog && (
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 pb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">{tr.actor}</Label>
                    <p className="font-medium">{selectedLog.actorName || selectedLog.actorEmail || tr.system}</p>
                    {selectedLog.actorEmail && <p className="text-xs text-muted-foreground">{selectedLog.actorEmail}</p>}
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">{tr.actionLabel}</Label>
                    <Badge className={ACTION_COLORS[selectedLog.action] || ""}>
                      {actionLabels[selectedLog.action] || selectedLog.action}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">{tr.entityType}</Label>
                    <p className="font-medium">{selectedLog.entityType}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">ID</Label>
                    <p className="font-mono text-xs">{selectedLog.entityId}</p>
                  </div>
                  {selectedLog.documentName && (
                    <div>
                      <Label className="text-muted-foreground text-xs">{tr.document}</Label>
                      <p className="font-medium">{selectedLog.documentName}</p>
                    </div>
                  )}
                  {selectedLog.actorIp && (
                    <div>
                      <Label className="text-muted-foreground text-xs">{tr.ipAddress}</Label>
                      <p className="font-mono text-sm">{selectedLog.actorIp}</p>
                    </div>
                  )}
                </div>
                {selectedLog.description && (
                  <div>
                    <Label className="text-muted-foreground text-xs">{tr.description}</Label>
                    <p className="text-sm bg-muted p-3 rounded-md">{selectedLog.description}</p>
                  </div>
                )}
                {selectedLog.metadata && (
                  <div>
                    <Label className="text-muted-foreground text-xs">{tr.metadata}</Label>
                    <pre className="bg-muted p-3 rounded-md text-xs overflow-auto max-h-40">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
